import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, fieldOptions, inboxApi, type ContactField, type Segment } from '@wa/shared';

/**
 * One customer, typed in — the third way a contact comes to exist, beside a
 * message arriving and a spreadsheet import.
 *
 * The number goes to the server as typed; the server applies the same rules the
 * import and the webhook use, so "+91 08948731278" lands on the row WhatsApp
 * will find when that person writes in. If the number is already a customer,
 * the server says which row, and the dialog offers to open it rather than
 * inventing a second one.
 *
 * Lists are added after the row exists (a second call per list), so a failure
 * there leaves a saved customer with fewer lists rather than no customer.
 */
export function AddCustomerDialog({ fields, segments, canPickLists, onClose, onCreated }: {
  fields: ContactField[];
  /** Static lists only; a rule-based list decides its own members. */
  segments: Segment[];
  canPickLists: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [lists, setLists] = useState<Set<number>>(new Set());
  const [existingId, setExistingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const staticLists = canPickLists ? segments.filter((sg) => sg.kind === 0) : [];

  const save = useMutation({
    mutationFn: async () => {
      const attributes: Record<string, string> = {};
      for (const [key, value] of Object.entries(values)) if (value.trim()) attributes[key] = value.trim();
      const created = await inboxApi.createContact({
        phone: phone.trim(),
        displayName: name.trim() || undefined,
        attributes: Object.keys(attributes).length ? attributes : undefined,
      });
      for (const listId of lists) await inboxApi.addSegmentMembers(listId, [created.id]);
      return created.id;
    },
    onSuccess: (id) => onCreated(id),
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as { contactId?: number } | undefined;
        setExistingId(typeof body?.contactId === 'number' ? body.contactId : null);
        setError('This number is already a customer.');
        return;
      }
      setExistingId(null);
      setError(e instanceof Error ? e.message : 'Could not add the customer.');
    },
  });

  const input = 'tap mt-1 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 py-2 text-body outline-none focus:border-(--color-brand)';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <form
        role="dialog" aria-label="Add customer" onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); if (phone.trim() && !save.isPending) { setError(null); setExistingId(null); save.mutate(); } }}
        className="flex max-h-[90vh] w-full max-w-sm flex-col rounded-(--radius-panel) bg-(--color-surface) shadow-(--shadow-pop)"
      >
        <header className="border-b border-(--color-line) px-4 py-3">
          <h3 className="text-lead font-semibold">Add customer</h3>
          <p className="mt-0.5 text-small text-(--color-muted)">Adding someone here does not message them. They can be broadcast to and will match when they write in.</p>
        </header>

        <div className="pane-scroll min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <label className="block text-small text-(--color-muted)">
            WhatsApp number
            <input
              autoFocus type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210" required className={input}
            />
          </label>
          <p className="mt-1 text-micro text-(--color-faint)">With the country code. A ten-digit Indian number is taken as +91.</p>

          <label className="mt-3 block text-small text-(--color-muted)">
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="Optional" className={input} />
          </label>

          {fields.map((f) => (
            <label key={f.id} className="mt-3 block text-small text-(--color-muted)">
              {f.label}
              <FieldInput field={f} value={values[f.fieldKey] ?? ''} onChange={(v) => setValues((cur) => ({ ...cur, [f.fieldKey]: v }))} className={input} />
            </label>
          ))}

          {staticLists.length > 0 && (
            <fieldset className="mt-4">
              <legend className="text-small text-(--color-muted)">Add to lists</legend>
              <ul className="mt-1 flex flex-col">
                {staticLists.map((sg) => (
                  <li key={sg.id}>
                    <label className="flex cursor-pointer items-center gap-2.5 py-1.5">
                      <input
                        type="checkbox" checked={lists.has(sg.id)}
                        onChange={(e) => setLists((cur) => { const next = new Set(cur); if (e.target.checked) next.add(sg.id); else next.delete(sg.id); return next; })}
                        className="size-4.5 accent-(--color-brand)"
                      />
                      <span className="text-body">{sg.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}

          {error && (
            <p role="alert" className="mt-3 text-small text-(--color-bad)">
              {error}
              {existingId !== null && (
                <> <button type="button" onClick={() => onCreated(existingId)} className="font-medium text-(--color-brand) hover:underline">Open them</button></>
              )}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-(--color-line) px-4 py-3">
          <button type="button" onClick={onClose} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
          <button type="submit" disabled={save.isPending || !phone.trim()} className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-60">
            {save.isPending ? 'Adding…' : 'Add customer'}
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldInput({ field, value, onChange, className }: { field: ContactField; value: string; onChange: (v: string) => void; className: string }) {
  if (field.fieldType === 'list') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={className}>
        <option value="">—</option>
        {fieldOptions(field).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.fieldType === 'boolean') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={className}>
        <option value="">—</option><option value="true">Yes</option><option value="false">No</option>
      </select>
    );
  }
  return (
    <input
      type={field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text'}
      value={value} onChange={(e) => onChange(e.target.value)} className={className}
    />
  );
}
